/**
 * @pg/section-renderer: fetch sections through Shopify's Section Rendering API
 * (`?section_id=` / `?sections=`) and swap them into the page.
 *
 * Every request for the same target is aborted when a newer one starts (fast filter clicks),
 * and GET responses are cached for the page's lifetime so going back to a filter is instant.
 *
 * Shopify subsets {% stylesheet %} CSS per page (only files rendered on that page), so a section
 * fetched later brings its own CSS in a <style data-section-stylesheet> tag. Callers usually keep
 * only part of the response, so every response's stylesheets are adopted into <head> here, once.
 */

/** @type {Set<string>} */
const adopted = new Set();

/**
 * Move the section stylesheets in fetched HTML into <head> (deduplicated), so components rendered
 * after load are styled. Accepts the HTML string or an already parsed document.
 * @param {string|Document} source
 */
export function adoptSectionStyles(source) {
  const css = typeof source === 'string'
    ? [...source.matchAll(/<style data-section-stylesheet[^>]*>([\s\S]*?)<\/style>/g)].map((m) => m[1])
    : [...source.querySelectorAll('style[data-section-stylesheet]')].map((el) => el.textContent || '');
  for (const text of css) {
    if (!text.trim() || adopted.has(text)) continue;
    adopted.add(text);
    const style = document.createElement('style');
    style.dataset.pgAdopted = '';
    style.textContent = text;
    document.head.append(style);
  }
}

/** @type {Map<string, Promise<string>>} */
const cache = new Map();
/** @type {Map<string, AbortController>} */
const inflight = new Map();

/**
 * Fetch one section's HTML at a URL.
 * @param {string} sectionId - The section's id (`section.id`)
 * @param {string} [url] - Page URL to render it for; defaults to the current page
 * @param {{ cache?: boolean, key?: string }} [options]
 * @returns {Promise<string>}
 */
export function fetchSection(sectionId, url = location.href, { cache: useCache = true, key = sectionId } = {}) {
  const target = new URL(url, location.origin);
  target.searchParams.set('section_id', sectionId);
  const href = target.toString();

  if (useCache && cache.has(href)) return /** @type {Promise<string>} */ (cache.get(href));

  inflight.get(key)?.abort();
  const controller = new AbortController();
  inflight.set(key, controller);

  const request = fetch(href, { signal: controller.signal }).then((response) => {
    if (!response.ok) throw new Error(`Section ${sectionId} failed: ${response.status}`);
    return response.text().then((html) => {
      adoptSectionStyles(html);
      return html;
    });
  });

  if (useCache) {
    cache.set(href, request);
    request.catch(() => cache.delete(href));
  }
  return request;
}

/**
 * Parse section HTML and return the element matching `selector` (default: the section root).
 * @param {string} html
 * @param {string} [selector]
 */
export function parseSection(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return selector ? doc.querySelector(selector) : doc.body.firstElementChild;
}

/**
 * Replace `target`'s children with the matching node from fresh section HTML.
 * Keeps focus on an element with the same id when possible.
 * @param {Element} target
 * @param {string} html
 * @param {string} [selector] - Selector of the replacement inside the HTML; defaults to `#${target.id}`
 */
export function replaceSection(target, html, selector = `#${CSS.escape(target.id)}`) {
  const next = parseSection(html, selector);
  if (!next) return;
  const activeId = document.activeElement?.id;
  target.replaceChildren(...next.childNodes);
  if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
}

/** Forget cached responses, e.g. after the cart changes prices or stock. */
export function clearSectionCache() {
  cache.clear();
}
